import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type NotificationLevel,
  type NotificationTypeRow,
  notificationPreferencesQueryKey,
  fetchNotificationPreferences,
  updateNotificationPreference,
} from './notificationPreferencesApi';

export type { NotificationTypeRow, NotificationLevel };

export interface PreferenceUpdate {
  notificationType: string;
  level: NotificationLevel;
  enabled: boolean;
}

export const useNotificationPreferences = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: notificationPreferencesQueryKey,
    queryFn: fetchNotificationPreferences,
  });

  // One mutation per click, even for the "All status changes" box: a batch
  // goes through as a single mutation so `isPending` covers every row in it
  // and a failure on any row is reported, rather than only the last row's
  // outcome being visible.
  const mutation = useMutation({
    mutationFn: async (updates: PreferenceUpdate[]) => {
      const results = await Promise.allSettled(
        updates.map(({ notificationType, level, enabled }) =>
          updateNotificationPreference(notificationType, level, enabled)
        )
      );
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      );
      if (failure) {
        throw failure.reason instanceof Error
          ? failure.reason
          : new Error('Failed to update notification preference');
      }
    },
    onSettled: () => {
      // Each click mutates independently; invalidating on every settle
      // keeps the whole list in sync with the server rather than trying to
      // patch just the one row that changed — and on a rejected update it
      // pulls back the value the server actually kept.
      queryClient.invalidateQueries({
        queryKey: notificationPreferencesQueryKey,
      });
    },
  });

  return {
    preferences: data ?? [],
    isLoading,
    error: error instanceof Error ? error.message : null,
    // Surfaced separately from the load error: a rejected toggle leaves the
    // checkbox showing the server's value after the invalidate, so without
    // this the row just silently snaps back.
    updateError:
      mutation.error instanceof Error ? mutation.error.message : null,
    updatePreferences: (updates: PreferenceUpdate[]) => {
      if (updates.length > 0) mutation.mutate(updates);
    },
    isUpdating: mutation.isPending,
  };
};
